import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { fireEvent } from '../../utils/funnelEvents';
import MobileDownloadCTA, { useIsPhone } from '../../components/MobileDownloadCTA';

// The hero repeats the law-firm ad's own words (ads/law-reddit-v1): a visitor
// arriving from the ad should see the same story on the first screen. Keep
// the claims here inside what the ad and the claim gate allow: "each
// document" (never "every"), OCR for scans, nothing moves until Apply.
//
// Most ad clicks are on phones (56 of the first 62), which cannot install
// Sortio, so a phone gets the email-me-the-link form in place of the
// download button instead of a hop to /download.
const LawFirmsHero = () => {
  const isPhone = useIsPhone();
  return (
    <section className="relative sm:min-h-[80vh] flex items-center text-apple-50 overflow-hidden bg-black pt-24 sm:pt-[140px] pb-8 sm:pb-20">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-[10%] left-[15%] w-[35vw] h-[35vw] rounded-full bg-sortio-secondary/20 blur-[120px] animate-float-slow" />
        <div className="absolute bottom-[10%] right-[15%] w-[40vw] h-[40vw] rounded-full bg-sortio-primary/20 blur-[150px] animate-float" />
      </div>

      <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20 text-center">
        <div className="hidden sm:inline-block mb-6">
          <div className="flex items-center justify-center px-4 py-1.5 rounded-full bg-sortio-primary/10 border border-sortio-primary/20">
            <span className="text-xs font-medium uppercase tracking-wider text-sortio-primary">Sortio for Law Firms</span>
          </div>
        </div>

        <h1 className="text-[2.25rem] sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight mb-6 sm:mb-8 leading-[1.05]">
          Filed by client and matter.{' '}
          <span className="apple-gradient-text whitespace-nowrap">Checked by you.</span>
        </h1>

        <p className="text-lg md:text-xl mb-8 sm:mb-10 text-apple-200 font-light max-w-2xl mx-auto leading-relaxed">
          Turn on Analyze contents and Sortio reads each document, even scans, finds the client and
          the matter, and renames each file your firm&apos;s way. You check the plan. Nothing moves
          until you click Apply.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {isPhone ? (
            <MobileDownloadCTA source="law_firms_hero" className="w-full text-left" />
          ) : (
            <Link
              to="/download"
              onClick={() =>
                fireEvent('cta_click', { cta_id: 'law_firms_hero', page: '/for-law-firms', target: '/download' })
              }
              className="apple-button text-base px-8 py-4 flex items-center justify-center w-full sm:w-auto"
            >
              Download free for Mac &amp; Windows <ArrowRight className="ml-2 flex-shrink-0" size={18} />
            </Link>
          )}
          <Link
            to="/law-firms-confidentiality"
            onClick={() =>
              fireEvent('cta_click', {
                cta_id: 'law_firms_hero_confidentiality',
                page: '/for-law-firms',
                target: '/law-firms-confidentiality',
              })
            }
            className="text-apple-50 hover:text-sortio-primary transition-all px-6 py-3 flex items-center justify-center border border-apple-300/30 rounded-full hover:border-sortio-primary w-full sm:w-auto"
          >
            Confidentiality: what gets sent <ChevronRight className="ml-1 flex-shrink-0" size={18} />
          </Link>
        </div>

        <p className="mt-6 text-sm text-apple-300 max-w-xl mx-auto">
          Free trial, no credit card required. Installs on Mac or Windows; on a phone, we&apos;ll
          email you the link.
        </p>
      </div>
    </section>
  );
};

export default LawFirmsHero;
